/**
 * ZenMarkup Privacy Analytics — Line Chart Hover module.
 * Hover overlay (dashed line, dot, tooltip), mouse event handlers.
 */

import { formatLabel } from './line-chart-axis.js';

let tooltip = null;

/**
 * Create the shared tooltip element (once).
 */
export function createTooltip() {
	if (tooltip) return;
	tooltip = document.createElement('div');
	tooltip.className = 'zmpa-chart-tooltip';
	tooltip.style.display = 'none';
	document.body.appendChild(tooltip);
}

/** Hide tooltip. */
export function hideTooltip() {
	if (tooltip) tooltip.style.display = 'none';
}

/**
 * Draw hover overlay: vertical dashed line, dot on point, highlighted X label.
 *
 * @param {Object} opts
 * @param {CanvasRenderingContext2D} opts.ctx
 * @param {number} opts.w           Canvas logical width.
 * @param {number} opts.h           Canvas logical height.
 * @param {Array}  opts.values      Current series values.
 * @param {number} opts.hoverIdx    Index of hovered data point.
 * @param {Array}  opts.timeseries  Full timeseries data.
 * @param {Object} opts.PAD         Padding object.
 * @param {string} opts.lineColor   Line/dot stroke color.
 * @param {string} opts.labelFont   Font string for labels.
 * @param {number} opts.dotRadius   Hover dot radius.
 * @param {number} opts.dotBorder   Hover dot border width.
 * @param {Function} opts.niceMax   Function to compute nice max value.
 */
export function drawOverlay(opts) {
	const ctx = opts.ctx;
	const w = opts.w;
	const h = opts.h;
	const values = opts.values;
	const hoverIdx = opts.hoverIdx;
	const PAD = opts.PAD;
	const maxVal = opts.niceMax(Math.max.apply(null, values));
	const chartW = w - PAD.left - PAD.right;
	const chartH = h - PAD.top - PAD.bottom;
	const len = values.length;
	const stepX = len > 1 ? chartW / (len - 1) : 0;
	const px = PAD.left + hoverIdx * stepX;
	const py = PAD.top + chartH - (chartH * (values[hoverIdx] / maxVal));

	// Vertical dashed line from point to X axis.
	ctx.save();
	ctx.strokeStyle = opts.lineColor;
	ctx.lineWidth = 1;
	ctx.setLineDash([4, 4]);
	ctx.beginPath();
	ctx.moveTo(px, py + opts.dotRadius);
	ctx.lineTo(px, PAD.top + chartH);
	ctx.stroke();
	ctx.restore();

	// Dot: white fill with blue border.
	ctx.beginPath();
	ctx.arc(px, py, opts.dotRadius, 0, Math.PI * 2);
	ctx.fillStyle = '#FFFFFF';
	ctx.fill();
	ctx.strokeStyle = opts.lineColor;
	ctx.lineWidth = opts.dotBorder;
	ctx.stroke();

	// Highlighted date label on X axis (bold, dark).
	const label = formatLabel(opts.timeseries[hoverIdx].date, len);
	ctx.font = 'bold ' + opts.labelFont;
	ctx.fillStyle = '#1E293B';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'top';
	ctx.fillText(label, px, h - PAD.bottom + 16);
}

/**
 * Handle mousemove on canvas: update hoverIdx, trigger redraw, position tooltip.
 *
 * @param {MouseEvent} e
 * @param {Object}     opts
 * @param {HTMLCanvasElement} opts.canvas
 * @param {Array}      opts.timeseries
 * @param {Object}     opts.PAD
 * @param {string}     opts.activeSeries
 * @param {Function}   opts.niceMax
 * @param {Function}   opts.formatYValue
 * @param {Function}   opts.getHoverIdx   Returns current hoverIdx.
 * @param {Function}   opts.setHoverIdx   Sets hoverIdx and triggers redraw.
 */
export function onMouseMove(e, opts) {
	if (!opts.timeseries.length || !tooltip) return;
	const rect = opts.canvas.getBoundingClientRect();
	const chartW = rect.width - opts.PAD.left - opts.PAD.right;
	const len = opts.timeseries.length;
	const stepX = len > 1 ? chartW / (len - 1) : 1;
	let idx = Math.round((e.clientX - rect.left - opts.PAD.left) / stepX);
	idx = Math.max(0, Math.min(idx, len - 1));

	if (idx === opts.getHoverIdx()) return;
	opts.setHoverIdx(idx);

	// Position tooltip above the dot.
	const maxVal = opts.niceMax(Math.max.apply(null, opts.timeseries.map(function (d) { return d[opts.activeSeries]; })));
	const chartH = rect.height - opts.PAD.top - opts.PAD.bottom;
	const px = opts.PAD.left + idx * stepX;
	const py = opts.PAD.top + chartH - (chartH * (opts.timeseries[idx][opts.activeSeries] / maxVal));

	tooltip.textContent = opts.formatYValue(opts.timeseries[idx][opts.activeSeries]);
	tooltip.style.display = 'block';

	const tipW = tooltip.offsetWidth;
	const tipH = tooltip.offsetHeight;
	tooltip.style.left = (rect.left + window.scrollX + px - tipW / 2) + 'px';
	tooltip.style.top = (rect.top + window.scrollY + py - tipH - 12) + 'px';
}

/**
 * Handle mouseleave: reset hover state and hide tooltip.
 *
 * @param {Function} setHoverIdx  Sets hoverIdx to -1 and triggers redraw.
 */
export function onMouseLeave(setHoverIdx) {
	setHoverIdx(-1);
	hideTooltip();
}
